import React, { useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  InputAdornment,
  Typography
} from '@mui/material';
import {
  Send as SendIcon,
  AttachFile as AttachFileIcon,
  EmojiEmotions as EmojiIcon,
  Mic as MicIcon
} from '@mui/icons-material';
import { addMessage, setTyping } from '../redux/slices/messagesSlice';

const MessageInput = ({ conversationId, currentUser }) => {
  const [message, setMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  const handleMessageChange = (event) => {
    const value = event.target.value;
    setMessage(value);
    
    // Gestion de l'indicateur de frappe
    if (value.length > 0 && !isTyping) {
      setIsTyping(true);
      dispatch(setTyping(true));
    }
    
    // Reset du timeout de frappe
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
    
    // Arrêter l'indicateur de frappe après 2 secondes d'inactivité
    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
      dispatch(setTyping(false));
    }, 2000);
  };

  const handleSendMessage = () => {
    if (message.trim() === '') return;
    
    // Créer le nouveau message
    const newMessage = {
      id: Date.now(), // Simple ID basé sur timestamp
      senderId: currentUser.id,
      content: message.trim(),
      timestamp: new Date(),
      read: false
    };
    
    // Dispatch du message
    dispatch(addMessage({
      conversationId,
      message: newMessage
    }));
    
    // Reset du champ de saisie
    setMessage('');
    setIsTyping(false);
    dispatch(setTyping(false));
    
    // Clear timeout
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileAttach = () => {
    // Mock pour l'instant - ouvrir le sélecteur de fichiers
    fileInputRef.current?.click();
  };

  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      // Mock - pour l'instant on simule juste l'envoi d'un message avec le nom du fichier
      const fileMessage = {
        id: Date.now(),
        senderId: currentUser.id,
        content: `📎 Fichier partagé: ${file.name}`,
        timestamp: new Date(),
        read: false
      };
      
      dispatch(addMessage({
        conversationId,
        message: fileMessage
      }));
    }
    
    // Reset du input file
    event.target.value = '';
  };

  const handleEmojiClick = () => {
    // Mock pour l'instant - ajouter un emoji simple
    setMessage(prev => prev + '😊');
  };

  const handleVoiceMessage = () => {
    // Mock pour l'instant - simuler un message vocal
    const voiceMessage = {
      id: Date.now(),
      senderId: currentUser.id,
      content: '🎤 Message vocal (fonctionnalité à venir)',
      timestamp: new Date(),
      read: false
    };
    
    dispatch(addMessage({
      conversationId,
      message: voiceMessage
    }));
  };

  return (
    <Paper className="message-input-container" elevation={2}>
      <Box className="message-input-wrapper">
        {/* Input file caché */}
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept="image/*,application/pdf,.doc,.docx"
        />
        
        {/* Boutons d'actions à gauche */}
        <Box className="input-actions-left">
          <Tooltip title="Joindre un fichier">
            <IconButton 
              onClick={handleFileAttach}
              className="action-button attach-button"
              size="small"
            >
              <AttachFileIcon />
            </IconButton>
          </Tooltip>
          
          <Tooltip title="Ajouter un emoji">
            <IconButton 
              onClick={handleEmojiClick}
              className="action-button emoji-button"
              size="small"
            >
              <EmojiIcon />
            </IconButton>
          </Tooltip>
        </Box>
        
        {/* Champ de saisie */}
        <TextField
          fullWidth
          multiline
          maxRows={4}
          variant="outlined"
          placeholder="Écrire un message..."
          value={message}
          onChange={handleMessageChange}
          onKeyPress={handleKeyPress}
          className="message-input-field"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <Box className="input-actions-right">
                  {message.trim() === '' ? (
                    <Tooltip title="Message vocal">
                      <IconButton 
                        onClick={handleVoiceMessage}
                        className="action-button voice-button"
                        size="small"
                      >
                        <MicIcon />
                      </IconButton>
                    </Tooltip>
                  ) : (
                    <Tooltip title="Envoyer">
                      <IconButton 
                        onClick={handleSendMessage}
                        className="action-button send-button"
                        size="small"
                        disabled={message.trim() === ''}
                      >
                        <SendIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>
              </InputAdornment>
            ),
          }}
        />
      </Box>
      
      {/* Indicateur de frappe */}
      {isTyping && (
        <Box className="typing-indicator">
          <Typography variant="caption" className="typing-text">
            En train d'écrire...
          </Typography>
        </Box>
      )}
    </Paper>
  );
};

export default MessageInput;